/**
 * Synchronous lookups against the build-time postal_areas snapshot.
 *
 * The snapshot lives in src/generated/postal-snapshot.json and is generated
 * by scripts/build-postal-snapshot.ts before each vite build. It's a stripped
 * copy of the postal_areas table — just the fields needed for SEO meta + first
 * paint (name, slug, parent, headline stats). Chart payloads stay in Supabase
 * and are fetched client-side via React Query.
 *
 * Used by:
 *  - prerendered-paths.ts to enumerate every postal route for vite-react-ssg
 *  - PostalCountyPage / PostalMunicipalityPage / PostalLocalityPage to render
 *    SEO + headline content synchronously during SSG
 */
// Relative import (not @/) because src/prerendered-paths.ts is loaded by Node
// when reading vite.config.ts — at that point Vite's path aliases haven't been
// wired up yet and `@/...` imports throw ERR_MODULE_NOT_FOUND.
import snapshotJson from '../generated/postal-snapshot.json';

export interface PostalAreaSlim {
  id: string;
  geo_type: 'county' | 'municipality' | 'locality';
  code: string;
  slug: string | null;
  parent_code: string | null;
  parent_geo_type: string | null;
  name: string;
  population_total: number;
  postcode_count: number;
  area: number | null;
}

const SNAPSHOT = snapshotJson as PostalAreaSlim[];

// Index by (geo_type, slug) and (geo_type, parent_code, slug) so lookups are
// O(1). Built lazily at module load.
const bySlugTop = new Map<string, PostalAreaSlim>(); // counties only
const bySlugWithParent = new Map<string, PostalAreaSlim>();
const byCode = new Map<string, PostalAreaSlim>();
const childrenByParentCode = new Map<string, PostalAreaSlim[]>();

for (const row of SNAPSHOT) {
  byCode.set(`${row.geo_type}:${row.code}`, row);

  if (row.geo_type === 'county' && row.slug) {
    bySlugTop.set(row.slug, row);
  } else if (row.slug && row.parent_code) {
    bySlugWithParent.set(
      `${row.geo_type}:${row.parent_code}:${row.slug}`,
      row
    );
  }

  if (row.parent_code) {
    const list = childrenByParentCode.get(row.parent_code) ?? [];
    list.push(row);
    childrenByParentCode.set(row.parent_code, list);
  }
}

export function getCountyBySlug(slug: string | undefined): PostalAreaSlim | null {
  if (!slug) return null;
  return bySlugTop.get(slug) ?? null;
}

export function getMunicipalityBySlug(
  countyCode: string | undefined,
  slug: string | undefined
): PostalAreaSlim | null {
  if (!countyCode || !slug) return null;
  return bySlugWithParent.get(`municipality:${countyCode}:${slug}`) ?? null;
}

export function getLocalityBySlug(
  municipalityCode: string | undefined,
  slug: string | undefined
): PostalAreaSlim | null {
  if (!municipalityCode || !slug) return null;
  return bySlugWithParent.get(`locality:${municipalityCode}:${slug}`) ?? null;
}

export function getCountyByCode(code: string): PostalAreaSlim | null {
  return byCode.get(`county:${code}`) ?? null;
}

export function getMunicipalityByCode(code: string): PostalAreaSlim | null {
  return byCode.get(`municipality:${code}`) ?? null;
}

export function getMunicipalitiesByCountyCode(
  countyCode: string | undefined
): PostalAreaSlim[] {
  if (!countyCode) return [];
  return (childrenByParentCode.get(countyCode) ?? []).filter(
    (r) => r.geo_type === 'municipality'
  );
}

export function getLocalitiesByMunicipalityCode(
  municipalityCode: string | undefined
): PostalAreaSlim[] {
  if (!municipalityCode) return [];
  return (childrenByParentCode.get(municipalityCode) ?? []).filter(
    (r) => r.geo_type === 'locality'
  );
}

export function listCounties(): PostalAreaSlim[] {
  return SNAPSHOT.filter((r) => r.geo_type === 'county');
}

export function listMunicipalities(): PostalAreaSlim[] {
  return SNAPSHOT.filter((r) => r.geo_type === 'municipality');
}

export function listLocalities(): PostalAreaSlim[] {
  return SNAPSHOT.filter((r) => r.geo_type === 'locality');
}
